import { Component, Input, signal, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LTPrimeNgModule } from 'src/app/third-party/lt-primeng.module';
import { ExportOptions } from 'src/app/features/grid/services/papa-parse-export-service.service';
import { ExportManagerFacade, ExportStats } from '../../classes/export-manager.facade';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { GridColumnData } from '../../classes/grid-column-data.class';

interface ColumnTreeNode {
  column: GridColumnData;
  children: ColumnTreeNode[];
  level: number;
  colspan: number;
  isLeaf: boolean;
}

interface PreviewHeaderRow {
  cells: PreviewHeaderCell[];
}

interface PreviewHeaderCell {
  value: string;
  colspan: number;
  level: number;
  isLeaf: boolean;
  field?: string;
}

@Component({
  selector: 'app-lt-export-preview',
  standalone: true,
  imports: [CommonModule, LTPrimeNgModule, MatCardModule, MatIconModule],
  templateUrl: './lt-export-preview.component.html',
  styleUrl: './lt-export-preview.component.scss',
})
export class LtExportPreviewComponent implements OnChanges {
  @Input() showPreview = false;
  @Input() exportStats!: ExportStats;
  @Input() exportOptions!: ExportOptions;
  @Input() exportManagerFacade?: ExportManagerFacade;
  @Input() selectedScope: 'all' | 'filtered' | 'selected' = 'all';
  @Input() columns?: GridColumnData[]; // Optional: for building nested preview

  maxRows = 5;
  totalRows = 0;

  previewLoading = signal<boolean>(false);
  previewError = signal<string>('');
  previewData = signal<any[]>([]);
  previewHeaders = signal<string[]>([]);
  aggregateRowIndices = signal<Set<number>>(new Set());
  
  // Nested column support
  nestedHeaderRows = signal<PreviewHeaderRow[]>([]);
  previewDataArray = signal<any[][]>([]);
  columnTree = signal<ColumnTreeNode[]>([]);

  ngOnChanges(changes: SimpleChanges): void {
    // Regenerate preview when relevant inputs change
    if (this.showPreview && this.exportManagerFacade) {
      const shouldRegenerate = 
        changes['showPreview']?.currentValue === true ||
        changes['exportOptions'] ||
        changes['selectedScope'];

      if (shouldRegenerate) {
        this.generatePreview();
      }
    }
  }

  generatePreview(): void {
    if (!this.exportManagerFacade || !this.showPreview) return;

    this.previewLoading.set(true);
    this.previewError.set('');

    this.exportManagerFacade.generatePreviewObservable(this.maxRows, this.exportOptions)
      .subscribe({
        next: (preview) => {
          this.previewData.set(preview);
          
          if (preview.length > 0) {
            if (this.exportOptions.flattenNestedHeaders) {
              // Flat headers
              this.previewHeaders.set(Object.keys(preview[0]));
            } else {
              // Nested headers - need to build structure
              this.buildNestedPreview(preview);
            }
            
            // Identify aggregate rows (they typically have specific labels)
            const aggregateIndices = new Set<number>();
            preview.forEach((row, index) => {
              const firstValue = Object.values(row)[0];
              if (this.isAggregateLabel(String(firstValue))) {
                aggregateIndices.add(index);
              }
            });
            this.aggregateRowIndices.set(aggregateIndices);
          }
          
          this.totalRows = this.getTotalRowCount();
          this.previewLoading.set(false);
        },
        error: (error) => {
          console.error('Failed to generate preview:', error);
          this.previewError.set('Failed to generate preview. Please try again.');
          this.previewLoading.set(false);
        },
      });
  }

  private buildNestedPreview(preview: any[]): void {
    // Convert preview data to array format
    const dataArrays = preview.map(row => Object.values(row));
    this.previewDataArray.set(dataArrays);
    
    // Build header structure if we have columns
    if (this.columns) {
      const tree = this.buildColumnTree(this.columns);
      this.columnTree.set(tree);
      
      const maxDepth = this.calculateMaxDepth(tree);
      const headerRows = this.generateHeaderRows(tree, maxDepth);
      this.nestedHeaderRows.set(headerRows);
    } else {
      // Fallback: use flat headers
      this.previewHeaders.set(Object.keys(preview[0]));
    }
  }

  private buildColumnTree(columns: GridColumnData[]): ColumnTreeNode[] {
    return columns
      .filter(col => col.exportable !== false)
      .map(col => this.buildColumnNode(col, 0));
  }

  private buildColumnNode(column: GridColumnData, level: number): ColumnTreeNode {
    const children = column.children?.length
      ? column.children
          .filter(child => child.exportable !== false)
          .map(child => this.buildColumnNode(child, level + 1))
      : [];
    
    const isLeaf = children.length === 0;
    const colspan = isLeaf ? 1 : this.calculateColspan(children);
    
    return {
      column,
      children,
      level,
      colspan,
      isLeaf,
    };
  }

  private calculateColspan(children: ColumnTreeNode[]): number {
    return children.reduce((sum, child) => sum + child.colspan, 0);
  }

  private calculateMaxDepth(nodes: ColumnTreeNode[]): number {
    let maxDepth = 1;
    
    const traverse = (node: ColumnTreeNode, currentDepth: number): void => {
      maxDepth = Math.max(maxDepth, currentDepth);
      node.children.forEach(child => traverse(child, currentDepth + 1));
    };
    
    nodes.forEach(node => traverse(node, 1));
    return maxDepth;
  }

  private generateHeaderRows(
    columnTree: ColumnTreeNode[], 
    maxDepth: number,
  ): PreviewHeaderRow[] {
    const headerRows: PreviewHeaderRow[] = [];
    
    for (let i = 0; i < maxDepth; i++) {
      headerRows.push({ cells: [] });
    }
    
    const fillHeaderRows = (nodes: ColumnTreeNode[], rowIndex: number): void => {
      nodes.forEach(node => {
        const headerRow = headerRows[rowIndex];
        
        headerRow.cells.push({
          value: node.column.header,
          colspan: node.colspan,
          level: node.level,
          isLeaf: node.isLeaf,
          field: node.isLeaf ? node.column.field : undefined,
        });
        
        if (node.isLeaf) {
          for (let i = rowIndex + 1; i < maxDepth; i++) {
            headerRows[i].cells.push({
              value: '',
              colspan: 1,
              level: i,
              isLeaf: true,
            });
          }
        } else {
          fillHeaderRows(node.children, rowIndex + 1);
        }
      });
    };
    
    fillHeaderRows(columnTree, 0);
    return headerRows;
  }

  private isAggregateLabel(value: string): boolean {
    const aggregateLabels = [
      'total', 'average', 'count', 'sum', 'avg', 'min', 'max',
      'selected total', 'selected average', 'selected count',
      'filtered total', 'filtered average', 'filtered count',
    ];
    return aggregateLabels.some(label => 
      value.toLowerCase().includes(label),
    );
  }

  isAggregateRow(row: any): boolean {
    const rowIndex = this.previewData().indexOf(row);
    return this.aggregateRowIndices().has(rowIndex);
  }

  isAggregateRowByIndex(index: number): boolean {
    return this.aggregateRowIndices().has(index);
  }

  getPreviewScope(): string {
    switch (this.selectedScope) {
      case 'selected':
        return `${this.exportStats.selectedRecords} selected records`;
      case 'filtered':
        return `${this.exportStats.filteredRecords} filtered records`;
      case 'all':
      default:
        return `${this.exportStats.totalRecords} total records`;
    }
  }

  getTotalRowCount(): number {
    switch (this.selectedScope) {
      case 'selected':
        return this.exportStats.selectedRecords;
      case 'filtered':
        return this.exportStats.filteredRecords;
      case 'all':
      default:
        return this.exportStats.totalRecords;
    }
  }

  getDataRowCount(): number {
    return this.previewData().length - this.aggregateRowIndices().size;
  }

  getAggregateRowCount(): number {
    return this.aggregateRowIndices().size;
  }

  hasAggregateRows(): boolean {
    return this.aggregateRowIndices().size > 0;
  }

  hasNestedColumns(): boolean {
    return this.columnTree().length > 0 && 
           this.columnTree().some(node => !node.isLeaf);
  }

  getHeaderRowCount(): number {
    return this.nestedHeaderRows().length;
  }

  async copyPreviewToClipboard(): Promise<void> {
    // try {
    //   const headers = this.previewHeaders().join('\t');
    //   const rows = this.previewData().map(row => 
    //     this.previewHeaders().map(header => row[header] || '').join('\t'),
    //   ).join('\n');
      
    //   const text = `${headers}\n${rows}`;
    //   await navigator.clipboard.writeText(text);
      
    //   console.log('Preview data copied to clipboard');
    //   // Optional: Show success message
    // } catch (error) {
    //   console.error('Failed to copy to clipboard:', error);
    // }
    try {
      let text: string;
      
      if (this.exportOptions.flattenNestedHeaders) {
        // Flat format
        const headers = this.previewHeaders().join('\t');
        const rows = this.previewData().map(row => 
          this.previewHeaders().map(header => row[header] || '').join('\t'),
        ).join('\n');
        text = `${headers}\n${rows}`;
      } else {
        // Nested format
        const headerRows = this.nestedHeaderRows().map(row =>
          row.cells.map(cell => cell.value || '').join('\t'),
        ).join('\n');
        
        const dataRows = this.previewDataArray().map(row =>
          row.join('\t'),
        ).join('\n');
        
        text = `${headerRows}\n${dataRows}`;
      }
      
      await navigator.clipboard.writeText(text);
      console.log('Preview data copied to clipboard');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  }
}
